import React, { useEffect, useRef, useState, useCallback } from "react";
import { fetchAds } from "./adService";
import { AdVideo } from "./AdVideo";
import TimelineEditor from "./TimelineEditor";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import dayjs from "dayjs";
const DEFAULT_IMAGE = "https://placehold.co/1920x1080/020617/94a3b8?text=NO+ADVERTISEMENT+SCHEDULED";

type Props = {
  onAdsDataChange?: (data: any[]) => void;
  currentAdIndex?: number;
  setCurrentAdIndex?: (index: number) => void;
};

const styles: { [key: string]: React.CSSProperties } = {
  mainLayout: {
    display: "block",
    padding: 0,
    height: "94vh",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  wrapper: {
    borderRadius: 18,
    overflow: "hidden",
    //border: "1px solid #172554",
    fontFamily: "Inter, sans-serif",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
  },
  videoWrapper: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    position: "relative",
  },
  videoContainer: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    borderRadius: 14,
    background: "#000",
  },
  secondaryButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backdropFilter: "blur(8px)",
  },
  playButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 0 18px rgba(37,99,235,0.5)",
  },
};

const AddPlayer: React.FC<Props> = ({
  onAdsDataChange,
  currentAdIndex,
  setCurrentAdIndex,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [ads, setAds] = useState<AdVideo[]>([]);
  const [internalIndex, setInternalIndex] = useState(-1);
  const [playing, setPlaying] = useState(true);
  const [isVertical, setIsVertical] = useState(false);
  const { activeCarDetails, selectedDate } = useSelector(
    (state: RootState) => state.service
  );

  const API_URL = activeCarDetails?.raw?.tunnel_info?.[0];

  const selectedIndex = currentAdIndex !== undefined ? currentAdIndex : internalIndex;

  const setSelectedIndex = (index: number) => {
    if (setCurrentAdIndex) {
      setCurrentAdIndex(index);
    } else {
      setInternalIndex(index);
    }
  };

  const loadAds = async () => {
    const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
    try {
      const data = await fetchAds({
        API_URL,
        formattedDate,
      });
      setAds(data || []);
      onAdsDataChange?.(data || []);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    loadAds();
    const interval = setInterval(() => {
      loadAds();
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedDate, API_URL]);

  const getCurrentAdIndex = (adList: AdVideo[]): number => {
    const now = dayjs();
    const index = adList.findIndex((ad) => {
      if (!ad.startTime || !ad.endTime) return false;
      const start = dayjs(ad.startTime);
      const end = dayjs(ad.endTime);
      return now.isAfter(start) && now.isBefore(end);
    });
    return index;
  };

  const playVideoNow = useCallback((url: string) => {
    const video = videoRef.current;
    if (!video) return;
    if (video.src === url) return;
    video.pause();
    video.src = url;
    video.load();
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { });
    }
  }, []);

  const switchToSlot = useCallback(
    (index: number, adList: AdVideo[]) => {
      const ad = adList[index];
      if (!ad) return;
      setSelectedIndex(index);
      if (ad.type === "video") {
        playVideoNow(ad.videoUrl);
      }
    },
    [playVideoNow]
  );

  useEffect(() => {
    if (ads.length === 0) return;
    const activeIndex = getCurrentAdIndex(ads);
    if (activeIndex >= 0) {
      switchToSlot(activeIndex, ads);
    }
  }, [ads]);

  useEffect(() => {
    syncTimerRef.current = setInterval(() => {
      if (ads.length === 0) return;
      const activeIndex = getCurrentAdIndex(ads);
      if (activeIndex === -1) {
        setSelectedIndex(-1);
        return;
      }
      if (activeIndex !== selectedIndex) {
        switchToSlot(activeIndex, ads);
      }
    }, 1000);

    return () => {
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
    };
  }, [ads, selectedIndex]);

  useEffect(() => {
    const mainVideo = videoRef.current;
    const bgVideo = bgVideoRef.current;
    if (playing) {
      mainVideo?.play().catch(() => { });
      bgVideo?.play().catch(() => { });
    } else {
      mainVideo?.pause();
      bgVideo?.pause();
    }
  }, [playing]);

  const handleNext = () => {
    if (ads.length === 0) return;
    const next = selectedIndex + 1 >= ads.length ? 0 : selectedIndex + 1;
    switchToSlot(next, ads);
  };

  const handlePrev = () => {
    if (ads.length === 0) return;
    const prev = selectedIndex <= 0 ? ads.length - 1 : selectedIndex - 1;
    switchToSlot(prev, ads);
  };

  const handleSelectVideo = (index: number) => {
    setSelectedIndex(index);
    const ad = ads[index];
    if (!ad) return;
    if (ad.type === "video") {
      playVideoNow(ad.videoUrl);
    }
  };

  const handleFullscreen = () => {
    const el = playerWrapperRef.current as any;
    if (!el) return;
    (
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen
    )?.call(el);
  };

  const handleAdsUpdate = (updatedAds: AdVideo[]) => {
    setAds(updatedAds);
    onAdsDataChange?.(updatedAds || []);
  };

  const currentAd = selectedIndex >= 0 ? ads[selectedIndex] : null;

  const handleVideoMetadata = (
    e: React.SyntheticEvent<HTMLVideoElement>
  ) => {
    const video = e.currentTarget;

    setIsVertical(
      video.videoHeight > video.videoWidth
    );
  };

  const syncBackgroundVideo = () => {
    const mainVideo = videoRef.current;
    const bgVideo = bgVideoRef.current;
    if (!mainVideo || !bgVideo) return;
    if (Math.abs(mainVideo.currentTime - bgVideo.currentTime) > 0.3) {
      bgVideo.currentTime = mainVideo.currentTime;
    }
  };

  if (ads.length === 0) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "#333",
          fontSize: 22,
          fontWeight: 700,
        }}
      >
        No Media Available
      </div>
    );
  }

  return (
    <div style={styles.mainLayout}>
      <div style={styles.wrapper}>
        <div style={styles.videoWrapper}>
          <div ref={playerWrapperRef} style={styles.videoContainer}>
            {currentAd ? (
              currentAd.type === "video" ? (
                <>
                  {isVertical && currentAd?.videoUrl && (
                    <video
                      ref={bgVideoRef}
                      src={currentAd.videoUrl}
                      muted
                      autoPlay
                      loop
                      playsInline
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "blur(10px)",
                        transform: "scale(1.2)",
                        opacity: 0.5,
                        zIndex: 1,
                      }}
                    />
                  )}
                  <video
                    ref={videoRef}
                    onTimeUpdate={syncBackgroundVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onLoadedMetadata={handleVideoMetadata}
                    style={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      height: "100%",
                      objectFit: isVertical ? "contain" : "cover",
                      background: "transparent",
                    }}
                  />
                </>
              ) : (
                <>
                  <img
                    src={currentAd.videoUrl}
                    alt=""
                    onLoad={(e) => {
                      const img = e.currentTarget;

                      setIsVertical(
                        img.naturalHeight > img.naturalWidth
                      );
                    }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: isVertical ? "blur(40px)" : "none",
                      transform: isVertical ? "scale(1.2)" : "none",
                      opacity: isVertical ? 0.8 : 1,
                      zIndex: 1,
                    }}
                  />

                  <img
                    src={currentAd.videoUrl}
                    alt={currentAd.title}
                    style={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      height: "100%",
                      objectFit: isVertical ? "contain" : "cover",
                    }}
                  />
                </>
              )
            ) : (
              <img
                src={DEFAULT_IMAGE}
                alt="No Schedule"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  background: "#000",
                }}
              />
            )}
          </div>
        </div>
        <TimelineEditor
          ads={ads}
          currentIndex={selectedIndex}
          onSelectVideo={handleSelectVideo}
          onAdsUpdate={handleAdsUpdate}
          API_URL={API_URL}
          onPrev={handlePrev}
          onNext={handleNext}
          onPlayPause={() => setPlaying((p) => !p)}
          onFullscreen={handleFullscreen}
          playing={playing}
        />
      </div>
    </div>
  );
};

export default AddPlayer; 